"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useUploadFiles = void 0;
const client_1 = require("@apollo/client");
const aws_auth_1 = require("@tbl/aws-auth");
const react_1 = require("react");
const uuid_1 = require("uuid");
const MapmakerApi_1 = require("../../client/MapmakerApi");
(0, client_1.gql) `
  fragment PresignedPost on PresignedPost {
    url
    fields
    expires
  }

  mutation createPresignedPost($folder: String) {
    createPresignedPost(folder: $folder) {
      ...PresignedPost
    }
  }
`;
// A slight buffer so that the presigned URL doesn't expire between when we check it and when we start the upload.
const EXPIRATION_BUFFER = 60 * 1000;
let cachedPresignedPost = null;
function setPresignedPost(presignedPost) {
    cachedPresignedPost = presignedPost;
}
function getPresignedPost(getNewPresignedPost) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!cachedPresignedPost ||
            new Date(cachedPresignedPost.expires).getTime() - EXPIRATION_BUFFER <
                new Date().getTime()) {
            const result = yield getNewPresignedPost();
            setPresignedPost(result);
        }
        return cachedPresignedPost;
    });
}
function useGetPresignedPost(folder) {
    const [getPresignedPost] = (0, MapmakerApi_1.useCreatePresignedPostMutation)({
        variables: {
            folder,
        },
    });
    return function () {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield getPresignedPost();
            return (_a = result.data) === null || _a === void 0 ? void 0 : _a.createPresignedPost;
        });
    };
}
function useUploadFiles(folder) {
    const getNewPresignedPost = useGetPresignedPost(folder);
    const uploadsRef = (0, react_1.useRef)([]);
    const { identityId } = (0, aws_auth_1.useAuthContext)();
    const [uploads, setUploads] = (0, react_1.useState)(uploadsRef.current);
    const results = (0, react_1.useMemo)(() => {
        return uploads
            .filter(upload => upload.status === "complete")
            .map(upload => upload.result);
    }, [uploads]);
    const uploadInProgress = (0, react_1.useMemo)(() => uploads.some(upload => upload.status === "uploading"), [uploads]);
    const addUpload = (0, react_1.useCallback)((newUpload) => {
        uploadsRef.current = [...uploadsRef.current, newUpload];
        setUploads(uploadsRef.current);
    }, [setUploads]);
    const updateUpload = (0, react_1.useCallback)((updatedUpload) => {
        uploadsRef.current = uploadsRef.current.map(upload => {
            if (upload.key === updatedUpload.key) {
                return updatedUpload;
            }
            else {
                return upload;
            }
        });
        setUploads(uploadsRef.current);
    }, [setUploads]);
    function removeUpload(key) {
        uploadsRef.current = uploadsRef.current.filter(upload => upload.key !== key);
        setUploads(uploadsRef.current);
    }
    const uploadFiles = (0, react_1.useCallback)((files) => __awaiter(this, void 0, void 0, function* () {
        const uploadPromises = files.map((file) => __awaiter(this, void 0, void 0, function* () {
            const rawExtension = (file.name.match(/\.([^.]*?)(?=\?|#|$)/) || [])[1];
            const extensionToAdd = rawExtension ? `.${rawExtension}` : "";
            const key = `${identityId}/uploads/${folder}/${(0, uuid_1.v4)()}${extensionToAdd}`;
            try {
                addUpload({
                    key,
                    filename: file.name,
                    status: "uploading",
                });
                const presignedPost = yield getPresignedPost(getNewPresignedPost);
                if (!presignedPost) {
                    throw new Error("Error uploading.");
                }
                const doUpload = new Promise((resolve, reject) => {
                    const formData = new FormData();
                    Object.keys(presignedPost.fields).forEach(fieldKey => {
                        formData.append(fieldKey, presignedPost.fields[fieldKey]);
                    });
                    formData.append("Key", key);
                    formData.append("Content-Type", file.type);
                    formData.append("acl", "public-read");
                    // Actual file has to be appended last.
                    formData.append("file", file);
                    const xhr = new XMLHttpRequest();
                    xhr.open("POST", presignedPost.url, true);
                    xhr.send(formData);
                    xhr.onload = function () {
                        this.status === 204
                            ? resolve(this.responseText)
                            : reject(this.responseText);
                    };
                });
                yield doUpload;
                updateUpload({
                    key,
                    filename: file.name,
                    status: "complete",
                    result: `${presignedPost.url}/${key}`,
                });
            }
            catch (e) {
                updateUpload({
                    key,
                    filename: file.name,
                    status: "error",
                    error: "There was an issue uploading your file.",
                });
            }
        }));
        yield Promise.allSettled(uploadPromises);
    }), [folder, getNewPresignedPost, addUpload, updateUpload]);
    return {
        uploadFiles,
        removeUpload,
        uploads,
        uploadInProgress,
        results,
    };
}
exports.useUploadFiles = useUploadFiles;
//# sourceMappingURL=data:application/json;base64,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